import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import PullToRefresh from "@/components/base/PullToRefresh";
import { SkeletonCard } from "@/components/base/Skeleton";
import { listSessions, listHoldings, type SessionHolding } from "@/api/alpha";
import { listSimOrders, placeSimOrder, getSimAccount, type SimOrder } from "@/api/sim";
import { getQuote } from "@/api/market";
import { searchResearchStocks } from "@/api/research";
import { useSubscription } from "@/hooks/useSubscription";
import PaywallModal from "@/components/feature/PaywallModal";

type TabType = "current" | "history";
type TradeAction = "加仓" | "减仓" | "平仓";
type StatusColor = "primary" | "red" | "green";

// Card shape consumed by the JSX below — derived from SessionHolding +
// its laddered exit plan so the existing markup stays untouched.
interface PositionCard {
  id: number;
  stockName: string;
  stockCode: string;
  // Full market-prefixed code (e.g. sh600519) used when placing sim orders;
  // the matching engine's normaliser also accepts the bare 6-digit form.
  ticker: string;
  shares: number;
  currentPrice: number;
  costPrice: number;
  marketValue: number;
  totalCost: number;
  profit: number;
  profitRate: number;
  positionPercent: number;
  aiStatus: string;
  aiStatusColor: StatusColor;
  stopProfit: { price: number; rate: number } | null;
  stopLoss: { price: number; rate: number } | null;
  triggered: string | null;
  sectors: string[];
}

interface TradeRow {
  id: number;
  stockName: string;
  stockCode: string;
  direction: "buy" | "sell";
  price: number;
  costPrice: number;
  shares: number;
  amount: number;
  realizedProfit?: number;
  realizedProfitRate: number;
  time: string;
  aiDecisionSummary: string;
  decisionType: "auto_stop" | "ai_suggest";
}

interface PositionsSummary {
  totalMarketValue: number;
  totalFloatingProfit: number;
  totalFloatingProfitRate: number;
  holdingCount: number;
}

function formatNum(n: number): string {
  return n.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPercent(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

// Strip the canonical market prefix (sh/sz/bj) so the route + display use the
// bare 6-digit code, matching the dashboard's /stock/:code convention.
function stripPrefix(code: string): string {
  return code.replace(/^[a-zA-Z]+/, "");
}

function formatTime(sec: number): string {
  if (!sec) return "";
  const d = new Date(sec * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function round1(n: number): number {
  return Number(n.toFixed(1));
}

// Map a live SessionHolding (+ exit plan) onto the card shape the UI expects.
function mapHolding(h: SessionHolding, idx: number): PositionCard {
  const avgCost = h.avgCost;
  const tp = h.exitPlan?.tpLadder?.[0] ?? null;
  const sl = h.exitPlan?.slLadder?.[0] ?? null;
  const anyTpFired = h.exitPlan?.tpLadder?.some((s) => s.triggered) ?? false;
  const anySlFired = h.exitPlan?.slLadder?.some((s) => s.triggered) ?? false;

  let triggered: string | null = null;
  if (anySlFired) triggered = "⚠ 已触发止损·待执行";
  else if (anyTpFired) triggered = "⚠ 已触发止盈·待执行";

  // Derive an AI status label from proximity to the nearest TP/SL rung.
  let aiStatus = "持有中";
  let aiStatusColor: StatusColor = "primary";
  if (anySlFired) {
    aiStatus = "计划减仓";
    aiStatusColor = "green";
  } else if (tp && h.currentPrice > 0 && h.currentPrice >= tp.price * 0.97) {
    aiStatus = "接近止盈";
    aiStatusColor = "red";
  } else if (sl && h.currentPrice > 0 && h.currentPrice <= sl.price * 1.03) {
    aiStatus = "接近止损";
    aiStatusColor = "green";
  }

  return {
    id: idx + 1,
    stockName: h.ticker,
    stockCode: stripPrefix(h.tickerCode),
    ticker: h.tickerCode,
    shares: h.shares,
    currentPrice: h.currentPrice,
    costPrice: avgCost,
    marketValue: h.marketValue,
    totalCost: avgCost * h.shares,
    profit: h.pnl,
    profitRate: h.pnlPct,
    positionPercent: round1(h.weight),
    aiStatus,
    aiStatusColor,
    stopProfit: tp && avgCost > 0 ? { price: tp.price, rate: round1(((tp.price - avgCost) / avgCost) * 100) } : null,
    stopLoss: sl && avgCost > 0 ? { price: sl.price, rate: round1(((sl.price - avgCost) / avgCost) * 100) } : null,
    triggered,
    sectors: h.industry ? [h.industry] : [],
  };
}

// Map a filled sim order onto the history-tab row shape.
function mapOrder(o: SimOrder): TradeRow {
  const amount = o.fillPrice * o.qty;
  const costTotal = (o.costBasis ?? 0) * o.qty;
  const realizedProfitRate = costTotal > 0 ? ((o.realizedPnL ?? 0) / costTotal) * 100 : 0;
  // Use the order's real reason; fall back to a neutral, factual label
  // (never a fabricated "AI 研判" tag) when the backend left it empty —
  // e.g. user-initiated sim orders carry no AI rationale.
  const reason = o.reason || (o.side === "buy" ? "买入成交" : "卖出成交");
  const decisionType: "auto_stop" | "ai_suggest" = /止损|止盈|风控|强平/.test(reason) ? "auto_stop" : "ai_suggest";
  return {
    id: o.id,
    stockName: o.name,
    stockCode: stripPrefix(o.ticker),
    direction: o.side,
    price: o.fillPrice,
    costPrice: o.costBasis ?? 0,
    shares: o.qty,
    amount,
    realizedProfit: o.side === "sell" ? o.realizedPnL : undefined,
    realizedProfitRate,
    time: formatTime(o.filledAt || o.placedAt),
    aiDecisionSummary: reason,
    decisionType,
  };
}

export default function Positions() {
  const navigate = useNavigate();
  const sub = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("current");
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
const [editingId, setEditingId] = useState<number | null>(null);
  const [detailSheet, setDetailSheet] = useState<{ type: "buy" | "sell" | null; stockId: number | null }>({ type: null, stockId: null });
  const [tradeConfirm, setTradeConfirm] = useState<{ action: TradeAction; pos: PositionCard } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tradeQty, setTradeQty] = useState(0);
  const [tradePrice, setTradePrice] = useState(0);
  const [tradePriceText, setTradePriceText] = useState("");
  const [toast, setToast] = useState("");
  const [cashBalance, setCashBalance] = useState(0);
  const [showAddStock, setShowAddStock] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addSuggestions, setAddSuggestions] = useState<any[]>([]);
  const [addShowSuggestions, setAddShowSuggestions] = useState(false);
  const [addSearching, setAddSearching] = useState(false);
  const [addSelectedIdx, setAddSelectedIdx] = useState(-1);
  const addSearchRef = useRef<HTMLDivElement>(null);
  const addTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [hasSession, setHasSession] = useState(true);
  const [currentPositions, setCurrentPositions] = useState<PositionCard[]>([]);
  const [historicalTrades, setHistoricalTrades] = useState<TradeRow[]>([]);
  const [positionsSummary, setPositionsSummary] = useState<PositionsSummary>({
    totalMarketValue: 0,
    totalFloatingProfit: 0,
    totalFloatingProfitRate: 0,
    holdingCount: 0,
  });
  const sessionIdRef = useRef(0);

  const loadData = useCallback(async () => {
    setError("");
    try {
      const { sessions } = await listSessions();
      const s = sessions.find((x) => x.status === "running") || sessions[0];
      if (!s) {
        setHasSession(false);
        setCurrentPositions([]);
        setHistoricalTrades([]);
        setPositionsSummary({ totalMarketValue: 0, totalFloatingProfit: 0, totalFloatingProfitRate: 0, holdingCount: 0 });
        return;
      }
      setHasSession(true);
      sessionIdRef.current = s.id;

      const [holdingsRes, ordersRes, accRes] = await Promise.all([
        listHoldings(s.id),
        listSimOrders(100),
        getSimAccount().catch(() => null),
      ]);
      if (accRes) setCashBalance(accRes.cashBalance);

      const cards = holdingsRes.holdings.map(mapHolding);
      setCurrentPositions(cards);

      const totalMarketValue = cards.reduce((acc, c) => acc + c.marketValue, 0);
      const totalFloatingProfit = cards.reduce((acc, c) => acc + c.profit, 0);
      const totalCost = cards.reduce((acc, c) => acc + c.totalCost, 0);
      setPositionsSummary({
        totalMarketValue,
        totalFloatingProfit,
        totalFloatingProfitRate: totalCost > 0 ? (totalFloatingProfit / totalCost) * 100 : 0,
        holdingCount: cards.length,
      });

      const trades = ordersRes.orders
        .filter((o) => o.status === "filled")
        .map(mapOrder);
      setHistoricalTrades(trades);
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  // ── Live polling: refresh holdings every 1s so prices/盈亏 update live ──
  useEffect(() => {
    if (!hasSession || sessionIdRef.current === 0) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      listHoldings(sessionIdRef.current).then(({ holdings }) => {
        const cards = holdings.map(mapHolding);
        setCurrentPositions(cards);
        const totalMarketValue = cards.reduce((acc, c) => acc + c.marketValue, 0);
        const totalFloatingProfit = cards.reduce((acc, c) => acc + c.profit, 0);
        const totalCost = cards.reduce((acc, c) => acc + c.totalCost, 0);
        setPositionsSummary({
          totalMarketValue,
          totalFloatingProfit,
          totalFloatingProfitRate: totalCost > 0 ? (totalFloatingProfit / totalCost) * 100 : 0,
          holdingCount: cards.length,
        });
      }).catch(() => {});
    };
    const timer = setInterval(tick, 10_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [hasSession]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const handleTradeAction = (action: TradeAction, pos: PositionCard) => {
    if (!sub.subscribed) { setShowPaywall(true); return; }
    setTradeConfirm({ action, pos });
    const held = Math.round(pos.shares);
    if (action === "加仓") setTradeQty(100);
    else if (action === "平仓") setTradeQty(held);
    else setTradeQty(Math.min(100, held));
    // 新股票默认用现价，用户可自行修改
    setTradePrice(pos.currentPrice || 0);
    setTradePriceText(pos.currentPrice > 0 ? String(pos.currentPrice) : "");
  };

  const doAddSearch = (q: string) => {
    if (addTimerRef.current) clearTimeout(addTimerRef.current);
    if (!q.trim()) { setAddSuggestions([]); setAddShowSuggestions(false); return; }
    addTimerRef.current = setTimeout(async () => {
      setAddSearching(true);
      try {
        const res = await searchResearchStocks(q.trim());
        setAddSuggestions(res.list || []);
        setAddShowSuggestions(res.list?.length > 0);
        setAddSelectedIdx(-1);
      } catch { setAddSuggestions([]); setAddShowSuggestions(false); }
      finally { setAddSearching(false); }
    }, 300);
  };

  const selectAddStock = async (stock: any) => {
    const code = stock.code || stock.stockCode || "";
    const name = stock.name || stock.stockName || code;
    if (!code) return;
    setShowAddStock(false);
    setAddSearch("");

    // Fetch live quote to pre-fill the price.
    // A-share 6-digit code needs exchange prefix for the market API.
    const prefixed = /^\d{6}$/.test(code)
      ? (/^(00[0-3]|30[0-2])/.test(code) ? "sz" + code : /^(6[0-9])/.test(code) ? "sh" + code : /^(8[0-9]|4[0-9])/.test(code) ? "bj" + code : code)
      : code;
    let currentPrice = 0;
    try {
      const res = await getQuote(prefixed);
      if (res?.quote?.price > 0) currentPrice = res.quote.price;
    } catch { /* ignore, user can fill manually */ }

    const newPos: PositionCard = {
      id: -1, stockName: name, stockCode: code, ticker: code,
      shares: 0, currentPrice, costPrice: 0, marketValue: 0,
      totalCost: 0, profit: 0, profitRate: 0, positionPercent: 0,
      aiStatus: "新增", aiStatusColor: "primary",
      stopProfit: null, stopLoss: null, triggered: null, sectors: [],
    };
    setTradeConfirm({ action: "加仓", pos: newPos });
    setTradeQty(100);
    setTradePrice(currentPrice);
    setTradePriceText(currentPrice > 0 ? String(currentPrice) : "");
  };

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if (!addShowSuggestions || addSuggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setAddSelectedIdx((p) => Math.min(p + 1, addSuggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAddSelectedIdx((p) => Math.max(p - 1, 0)); }
    else if (e.key === "Enter" && addSelectedIdx >= 0) { e.preventDefault(); selectAddStock(addSuggestions[addSelectedIdx]); }
    else if (e.key === "Escape") { setAddShowSuggestions(false); }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (addSearchRef.current && !addSearchRef.current.contains(e.target as Node)) setAddShowSuggestions(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Submit a real sim order against the user's simulated book — the same
  // sim_positions ledger these holdings are read from, so the change shows
  // up on the next refresh. 加仓 buys one lot (100 股), 减仓 sells one lot
  // (capped at the held quantity), 平仓 sells the whole position. The
  // matching engine enforces trading hours / T+1 / suspension and returns a
  // rejection reason we surface verbatim.
  const confirmTrade = useCallback(async () => {
    if (!tradeConfirm) return;
    const { action, pos } = tradeConfirm;
    const side: "buy" | "sell" = action === "加仓" ? "buy" : "sell";
    const held = Math.round(pos.shares);
    const qty = tradeQty;
    if (qty <= 0) {
      setTradeConfirm(null);
      return;
    }
    setSubmitting(true);
    try {
      const orderBody: any = { ticker: pos.ticker, side, qty };
      // 所有操作支持自定义价格
      if (tradePrice > 0) orderBody.price = tradePrice;
      const res = await placeSimOrder(orderBody);
      setToast(`已${action} ${pos.stockName} ${qty}股 @¥${res.fillPrice.toFixed(2)}`);
      await loadData();
    } catch (e) {
      setToast(e instanceof Error ? e.message : `${action}失败`);
    } finally {
      setSubmitting(false);
      setTradeConfirm(null);
    }
  }, [tradeConfirm, tradeQty, tradePrice, loadData]);

  return (
    <div className="pb-[80px]">
      {/* Header */}
      <PageHeader
        title="持仓"
        rightAction={
          <span className="text-[10px] text-accent-500 bg-accent-100/80 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
            模拟
          </span>
        }
      />

      {/* Segment Control */}
      <div className="px-4 mb-3">
        <div className="flex bg-background-100 rounded-full p-1">
          <button
            onClick={() => setActiveTab("current")}
            className={`flex-1 py-2 text-sm rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === "current"
                ? "bg-white text-foreground-950"
                : "text-foreground-500"
            }`}
          >
            当前持仓（{positionsSummary.holdingCount}）
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 py-2 text-sm rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === "history"
                ? "bg-white text-foreground-950"
                : "text-foreground-500"
            }`}
          >
            历史交易
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="px-4 space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="px-4 py-16 text-center">
          <p className="text-sm text-foreground-500">{error}</p>
          <button
            onClick={() => { setLoading(true); loadData().finally(() => setLoading(false)); }}
            className="mt-3 text-xs text-primary-500 font-medium cursor-pointer"
          >
            重试
          </button>
        </div>
      )}

      {/* No session */}
      {!loading && !error && !hasSession && (
        <div className="px-4 py-16 text-center">
          <p className="text-sm text-foreground-500">还没有运行中的模拟盘</p>
          <button
            onClick={() => navigate("/")}
            className="mt-3 text-xs text-primary-500 font-medium cursor-pointer"
          >
            去 AI 看盘开启模拟盘
          </button>
        </div>
      )}

      {!loading && !error && hasSession && activeTab === "current" && (
        <PullToRefresh onRefresh={handleRefresh}>
          {/* Summary Bar */}
          <div className="px-4 mb-3">
            <div className="bg-white rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-foreground-500">总市值</p>
                <p className="text-lg font-bold text-foreground-950">¥{formatNum(positionsSummary.totalMarketValue)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-foreground-500">总浮动盈亏</p>
                <p className={`text-lg font-bold ${positionsSummary.totalFloatingProfit >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                  {positionsSummary.totalFloatingProfit >= 0 ? "+" : ""}¥{formatNum(positionsSummary.totalFloatingProfit)}
                  <span className="text-sm ml-1">({formatPercent(positionsSummary.totalFloatingProfitRate)})</span>
                </p>
              </div>
            </div>
          </div>

          {/* Swipe hint */}
          <div className="px-4 mb-2">
            <p className="text-[10px] text-foreground-400 text-center">手动增仓新股</p>
          <div className="px-4 mt-2">
            <button onClick={() => setShowAddStock(true)}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-primary-500 to-accent-500 text-white cursor-pointer hover:opacity-90 transition-opacity">
              <i className="ri-add-line text-sm mr-1" />新增持仓
            </button>
          </div>
          </div>

          {/* Empty state */}
          {currentPositions.length === 0 && (
            <div className="px-4 py-12 text-center">
              <p className="text-sm text-foreground-500">暂无持仓</p>
              <p className="text-[10px] text-foreground-400 mt-1">AI 正在等待入场时机</p>
            </div>
          )}

          {/* Position Cards */}
          <div className="px-4 space-y-3">
            {currentPositions.map((pos) => (
              <div key={pos.id} className="bg-white rounded-xl border border-background-200/60 overflow-hidden">
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/stock/${pos.stockCode}`)}
                        className="text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors cursor-pointer text-left whitespace-nowrap"
                      >
                        {pos.stockName}
                      </button>
                      <span className="text-xs text-foreground-400">{pos.stockCode}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      pos.aiStatusColor === "primary" ? "bg-primary-100 text-primary-700" :
                      pos.aiStatusColor === "red" ? "bg-red-50 text-[#E11D48]" :
                      "bg-green-50 text-[#10B981]"
                    }`}>
                      {pos.aiStatus}
                    </span>
                    <button
                      onClick={() => setEditingId(editingId === pos.id ? null : pos.id)}
                      className="text-[10px] px-2 py-1 rounded-full bg-background-100 text-foreground-500 cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">
                      编辑
                    </button>
                  </div>

                  {editingId === pos.id && (
                    <div className="flex gap-2 mb-3 animate-fade-in">
                      <button onClick={() => { setEditingId(null); handleTradeAction("加仓", pos); }}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold text-white bg-[#E11D48] cursor-pointer hover:opacity-90 transition-opacity">加仓</button>
                      <button onClick={() => { setEditingId(null); handleTradeAction("减仓", pos); }}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold text-white bg-[#F59E0B] cursor-pointer hover:opacity-90 transition-opacity">减仓</button>
                      <button onClick={() => { setEditingId(null); handleTradeAction("平仓", pos); }}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold text-white bg-[#6B7280] cursor-pointer hover:opacity-90 transition-opacity">平仓</button>
                    </div>
                  )}

                  {/* Price Row */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>
                      <p className="text-[10px] text-foreground-400">现价</p>
                      <p className="text-sm font-semibold text-foreground-950">¥{pos.currentPrice.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400">成本</p>
                      <p className="text-sm text-foreground-700">¥{pos.costPrice.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400">仓位</p>
                      <p className="text-sm text-foreground-700">{pos.positionPercent}%</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <p className="text-[10px] text-foreground-400">持仓数量</p>
                      <p className="text-sm text-foreground-700">{pos.shares}股</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400">市值</p>
                      <p className="text-sm text-foreground-700">¥{formatNum(pos.marketValue)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400">盈亏</p>
                      <p className={`text-sm font-semibold ${pos.profit >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                        {formatPercent(pos.profitRate)}
                      </p>
                    </div>
                  </div>

                  {/* Risk Ladder */}
                  <div className="flex items-center gap-3 mb-2">
                    {pos.stopProfit && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E11D48]/10 text-[#E11D48] font-medium whitespace-nowrap">
                        TP ¥{pos.stopProfit.price.toFixed(2)}(+{pos.stopProfit.rate}%)
                      </span>
                    )}
                    {pos.stopLoss && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#10B981]/10 text-[#10B981] font-medium whitespace-nowrap">
                        SL ¥{pos.stopLoss.price.toFixed(2)}({pos.stopLoss.rate}%)
                      </span>
                    )}
                    {pos.triggered && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-[#E11D48] font-medium animate-pulse whitespace-nowrap">
                        {pos.triggered}
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex items-center gap-1.5 mb-3">
                    {pos.sectors.map((s) => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                        {s}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-background-200">
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedCard(expandedCard === pos.id ? null : pos.id); }}
                      className="text-xs text-primary-500 font-medium cursor-pointer whitespace-nowrap"
                    >
                      决策过程
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDetailSheet({ type: "buy", stockId: pos.id }); }}
                      className="text-xs text-foreground-500 cursor-pointer whitespace-nowrap"
                    >
                      买入明细
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDetailSheet({ type: "sell", stockId: pos.id }); }}
                      className="text-xs text-foreground-500 cursor-pointer whitespace-nowrap"
                    >
                      卖出明细
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PullToRefresh>
      )}

      {!loading && !error && hasSession && activeTab === "history" && (
        <div className="px-4 space-y-3">
          {historicalTrades.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-sm text-foreground-500">暂无历史交易</p>
            </div>
          )}
          {historicalTrades.map((trade) => (
            <div key={trade.id} className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/stock/${trade.stockCode}`)}
                    className="text-sm font-semibold text-foreground-950 hover:text-primary-500 transition-colors cursor-pointer text-left whitespace-nowrap"
                  >
                    {trade.stockName}
                  </button>
                  <span className="text-xs text-foreground-400">{trade.stockCode}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  trade.direction === "buy" ? "bg-[#E11D48]/10 text-[#E11D48]" : "bg-[#10B981]/10 text-[#10B981]"
                }`}>
                  {trade.direction === "buy" ? "买入" : "卖出"}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <p className="text-[10px] text-foreground-400">价格</p>
                  <p className="text-sm font-semibold text-foreground-950">¥{trade.price.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400">数量</p>
                  <p className="text-sm text-foreground-700">{trade.shares}股</p>
                </div>
                <div>
                  <p className="text-[10px] text-foreground-400">金额</p>
                  <p className="text-sm text-foreground-700">¥{formatNum(trade.amount)}</p>
                </div>
              </div>

              {trade.direction === "sell" && trade.realizedProfit !== undefined && (
                <div className="mb-2">
                  <p className="text-[10px] text-foreground-400">成本 → 实现盈亏</p>
                  <p className={`text-sm font-semibold ${trade.realizedProfit >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                    ¥{trade.costPrice.toFixed(2)} → {formatPercent(trade.realizedProfitRate)}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-background-200">
                <span className="text-[10px] text-foreground-400">{trade.time}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  trade.decisionType === "auto_stop" ? "bg-secondary-100 text-secondary-900" : "bg-primary-100 text-primary-700"
                }`}>
                  {trade.aiDecisionSummary}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Sheet Overlay */}
      {detailSheet.type && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center px-4" onClick={() => setDetailSheet({ type: null, stockId: null })}>
          <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-950">
                {detailSheet.type === "buy" ? "买入明细" : "卖出明细"}
              </h3>
              <button onClick={() => setDetailSheet({ type: null, stockId: null })}
                className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500 text-sm" />
              </button>
            </div>
            {(() => {
              const detailPos = currentPositions.find((p) => p.id === detailSheet.stockId);
              const detailTrades = detailPos
                ? historicalTrades.filter(
                    (t) => t.stockCode === detailPos.stockCode && t.direction === detailSheet.type,
                  )
                : [];
              if (detailTrades.length === 0) {
                return (
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {detailSheet.type === "buy" ? "暂无买入成交记录。" : "暂无卖出成交记录。"}
                    所有交易均为模拟交易，不涉及真实资金。
                  </p>
                );
              }
              return (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {detailTrades.map((t) => (
                    <div key={t.id} className="flex items-center justify-between border-b border-background-100 pb-2">
                      <div>
                        <p className="text-sm text-foreground-800">¥{t.price.toFixed(2)} × {t.shares}股</p>
                        <p className="text-[10px] text-foreground-400">{t.time}</p>
                      </div>
                      <p className="text-sm text-foreground-700">¥{formatNum(t.amount)}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
            <button
              onClick={() => setDetailSheet({ type: null, stockId: null })}
              className="mt-4 w-full py-3 bg-background-100 rounded-lg text-sm text-foreground-700 font-medium cursor-pointer"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {/* Trade Confirm Sheet */}
      {tradeConfirm && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center px-4" onClick={() => setTradeConfirm(null)}>
          <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-foreground-950">
                确认{tradeConfirm.action}
              </h3>
              <button onClick={() => setTradeConfirm(null)} className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500 text-sm" />
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-3">
              确定要对 <span className="font-semibold text-foreground-800">{tradeConfirm.pos.stockName}</span> 执行「{tradeConfirm.action}」操作吗？
            </p>
            <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-foreground-600 whitespace-nowrap">{tradeConfirm.action === "加仓" ? "成本价" : "卖出价"}</span>
                <div className="flex-1">
                  <input type="number" value={tradePriceText}
                    onChange={(e) => { setTradePriceText(e.target.value); setTradePrice(parseFloat(e.target.value) || 0); }}
                    className="w-full bg-background-50 rounded-lg py-2 px-3 text-sm font-semibold text-foreground-950 outline-none border-none text-right"
                    placeholder="输入价格" step="0.01" min={0.01} />
                </div>
                <button
                  onClick={() => { setTradePrice(tradeConfirm.pos.currentPrice || 0); setTradePriceText(String(tradeConfirm.pos.currentPrice || "")); }}
                  className="px-2 py-1 rounded text-[10px] font-medium bg-sky-50 text-sky-600 whitespace-nowrap cursor-pointer flex-shrink-0"
                >
                  现价
                </button>
                <span className="text-xs text-foreground-400">元/股</span>
              </div>
            {(() => {
              const price = tradePrice || tradeConfirm.pos.currentPrice || 0;
              const totalAmount = price * tradeQty;
              const isBuy = tradeConfirm.action === "加仓";
              const overBudget = isBuy && cashBalance > 0 && totalAmount > cashBalance;
              return (
                <div className="mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground-600">{isBuy ? "预估总金额" : "预估收入"}</span>
                    <div className="flex-1 text-right bg-background-50 rounded-lg py-2 px-3">
                      <span className="text-sm font-semibold text-foreground-950">¥{totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                  {isBuy && (
                    <div className="flex justify-between text-xs mt-1 px-1">
                      <span className="text-foreground-400">可用资金 ¥{cashBalance.toFixed(2)}</span>
                      {overBudget && (
                        <span className="text-[#E11D48] font-medium">超出可用资金 ¥{(totalAmount - cashBalance).toFixed(2)}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm text-foreground-600">数量</span>
              <div className="flex items-center gap-2 flex-1">
                <button onClick={() => setTradeQty(Math.max(100, tradeQty - 100))}
                  className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors text-foreground-600 text-sm font-semibold">-</button>
                <input type="number" value={tradeQty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 100;
                    const maxVal = tradeConfirm.action === "加仓" ? 9999999 : Math.round(tradeConfirm.pos.shares);
                    setTradeQty(Math.min(Math.max(100, val), maxVal));
                  }}
                  className="flex-1 text-center bg-background-50 rounded-lg py-2 text-sm font-semibold text-foreground-950 outline-none border-none"
                  min={100} />
                <button onClick={() => {
                  const maxVal = tradeConfirm.action === "加仓" ? 9999999 : Math.round(tradeConfirm.pos.shares);
                  setTradeQty(Math.min(maxVal, tradeQty + 100));
                }}
                  className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors text-foreground-600 text-sm font-semibold">+</button>
              </div>
              <span className="text-xs text-foreground-400">股</span>
            </div>

            <p className="text-[10px] text-foreground-400 mb-4 p-2 bg-background-50 rounded-lg">
              模拟交易 · 不涉及真实资金
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setTradeConfirm(null)}
                disabled={submitting}
                className="flex-1 py-3 bg-background-100 rounded-lg text-sm text-foreground-600 font-medium cursor-pointer disabled:opacity-50"
              >
                取消
              </button>
              {(() => {
                const price = tradePrice || tradeConfirm.pos.currentPrice || 0;
                const overBudget = tradeConfirm.action === "加仓" && cashBalance > 0 && price * tradeQty > cashBalance;
                return (
                  <button
                    onClick={confirmTrade}
                    disabled={submitting || overBudget}
                    className={`flex-1 py-3 rounded-lg text-sm text-white font-medium cursor-pointer disabled:opacity-60 ${
                      tradeConfirm.action === "加仓" ? "bg-[#E11D48]" :
                      tradeConfirm.action === "减仓" ? "bg-[#F59E0B]" :
                      "bg-[#6B7280]"
                    }`}
                  >
                    {submitting ? "提交中…" : overBudget ? "余额不足" : `确认${tradeConfirm.action}`}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddStock && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setShowAddStock(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">新增持仓</h2>
              <button onClick={() => setShowAddStock(false)} className="w-7 h-7 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500 text-sm" />
              </button>
            </div>
            <div className="relative" ref={addSearchRef}>
              <input value={addSearch}
                onChange={(e) => { setAddSearch(e.target.value); doAddSearch(e.target.value); }}
                onKeyDown={handleAddKeyDown}
                onFocus={() => { if (addSuggestions.length > 0) setAddShowSuggestions(true); }}
                placeholder="输入股票代码或名称搜索"
                autoFocus
                className="w-full bg-background-100 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none border-none" />
              {addSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />}
              {addShowSuggestions && addSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-background-200/60 shadow-lg z-50 max-h-[240px] overflow-y-auto">
                  {addSuggestions.map((s, i) => (
                    <button key={s.code || i}
                      onMouseDown={() => selectAddStock(s)}
                      onMouseEnter={() => setAddSelectedIdx(i)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${i === addSelectedIdx ? "bg-primary-50" : "hover:bg-background-50"}`}>
                      <span className="text-sm font-semibold text-foreground-950">{s.name || s.stockName}</span>
                      <span className="text-[10px] text-foreground-400 font-mono">{s.code || s.stockCode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[80] max-w-[88%] px-4 py-2 bg-foreground-950/90 text-white text-xs rounded-full shadow-lg text-center whitespace-nowrap overflow-hidden text-ellipsis">
          {toast}
        </div>
      )}

      {/* Disclaimer */}
      <div className="px-4 mt-4 mb-4">
        <p className="text-[10px] text-foreground-400 text-center">
          模拟 · 仅供学习参考，不构成投资建议
        </p>
      </div>
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} description="交易操作需要联系客服开通" />
    </div>
  );
}
